import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'motion/react';
import { LunarGridLogo } from '@/app/components/LunarGridLogo';
import { getAllProjects, createProject, updateProject, deleteProject, getAvailableTables, type Project } from '@/lib/api';
import { Plus, ArrowRight, Edit2, Trash2, Loader2, LayoutGrid, LogOut } from 'lucide-react';
import { ThemeToggle, ThemeNameToggle } from '@/app/components/ThemeToggle';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/app/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { toast } from 'sonner';

export default function ProjectsView() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [projectName, setProjectName] = useState('');
  const [selectedTable, setSelectedTable] = useState('__none__');
  const [availableTables, setAvailableTables] = useState<{ table_name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadProjects = async () => {
    try {
      const data = await getAllProjects();
      setProjects(data);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableTables = async () => {
    try {
      const tables = await getAvailableTables();
      setAvailableTables(tables);
    } catch {
      setAvailableTables([{ table_name: 'cities_france' }]);
    }
  };

  useEffect(() => {
    loadProjects();
    loadAvailableTables();
  }, []);

  const handleCreateProject = async () => {
    if (!projectName.trim()) return;
    setIsSaving(true);
    try {
      const newProject = await createProject({
        name: projectName.trim(),
        source_table_name: (selectedTable && selectedTable !== '__none__') ? selectedTable : undefined,
      });
      setIsCreateDialogOpen(false);
      setProjectName('');
      setSelectedTable('__none__');
      navigate(`/projects/${newProject.id}/variables`);
    } catch {
      toast.error('Failed to create project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateProject = async () => {
    if (!editingProject || !projectName.trim()) return;
    setIsSaving(true);
    try {
      await updateProject(editingProject.id, {
        name: projectName.trim(),
        source_table_name: (selectedTable && selectedTable !== '__none__') ? selectedTable : undefined,
      });
      setIsEditDialogOpen(false);
      setEditingProject(null);
      setProjectName('');
      await loadProjects();
    } catch {
      toast.error('Failed to update project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      await loadProjects();
      toast.success('Project deleted');
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const openEditDialog = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingProject(project);
    setProjectName(project.name);
    setSelectedTable(project.source_table_name || '__none__');
    setIsEditDialogOpen(true);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.97 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] as const } },
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.div
        className="border-b border-border bg-card/90 backdrop-blur-md"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] as const }}
      >
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <LunarGridLogo size="md" />
          <div className="flex items-center gap-2">
            <ThemeNameToggle />
            <ThemeToggle />
            <button
              onClick={() => {
                localStorage.removeItem('lunar-grid-authenticated');
                navigate('/login');
              }}
              title="Log out"
              className="theme-toggle"
            >
              <LogOut className="size-3.5" />
            </button>
            <Button
              size="sm"
              onClick={() => setIsCreateDialogOpen(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="size-4 mr-1.5" />
              New Project
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        className="max-w-5xl mx-auto px-6 py-10"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.1 }}
      >
        <div className="mb-8">
          <h1 className="font-display text-2xl font-bold text-foreground">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Each project links to a data source and contains its own template sets.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="size-6 animate-spin text-primary" />
          </div>
        ) : projects.length === 0 ? (
          <motion.div
            className="flex flex-col items-center justify-center py-24 text-center"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] as const, delay: 0.2 }}
          >
            <div className="size-14 rounded-xl bg-card border border-border flex items-center justify-center mb-4 card-accent">
              <LayoutGrid className="size-6 text-primary" />
            </div>
            <h3 className="font-display font-semibold text-foreground mb-1">No projects yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-xs">
              Create your first project to start generating Google Ads campaigns at scale.
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="size-4 mr-2" />
              Create First Project
            </Button>
          </motion.div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.18 } } }}
          >
            {projects.map((project) => (
              <motion.div key={project.id} variants={cardVariants}>
              <div
                onClick={() => navigate(`/projects/${project.id}/variables`)}
                className="group relative bg-card border border-border rounded-lg p-5 cursor-pointer card-hover h-full"
              >
                {/* Project name */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h3 className="font-display font-semibold text-foreground text-base leading-snug group-hover:text-primary transition-colors">
                    {project.name}
                </h3>
                  <ArrowRight className="size-4 text-muted-foreground/0 group-hover:text-primary/60 transition-all duration-200 shrink-0 mt-0.5 -translate-x-1 group-hover:translate-x-0" />
                </div>

                {/* Meta */}
                <div className="space-y-1 mb-4">
                  {project.source_table_name && (
                    <p className="text-xs font-mono text-muted-foreground/70">
                      {project.source_table_name}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground/50">
                    Created {new Date(project.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                </div>

                {/* Actions */}
                <div
                  className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={(e) => openEditDialog(project, e)}
                  >
                    <Edit2 className="size-3 mr-1" />
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="size-3 mr-1" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{project.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete all cities, templates, and generation logs
                          associated with this project. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => handleDeleteProject(project.id)}
                        >
                          Delete Project
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </motion.div>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Give your project a name. Source table is optional — skip it if you plan to use Quick Generate only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name</Label>
              <Input
                id="project-name"
                placeholder="e.g., Fleuriste — France"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="source-table">Source Table <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Select value={selectedTable} onValueChange={setSelectedTable}>
                <SelectTrigger id="source-table">
                  <SelectValue placeholder="No source table" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No source table</SelectItem>
                  {availableTables.map((t) => (
                    <SelectItem key={t.table_name} value={t.table_name}>
                      {t.table_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProject} disabled={!projectName.trim() || isSaving}>
              {isSaving && <Loader2 className="size-4 mr-2 animate-spin" />}
              Create Project
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>Update the project name or source table.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Project Name</Label>
              <Input
                id="edit-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdateProject()}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-table">Source Table <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Select value={selectedTable} onValueChange={setSelectedTable}>
                <SelectTrigger id="edit-table">
                  <SelectValue placeholder="No source table" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No source table</SelectItem>
                  {availableTables.map((t) => (
                    <SelectItem key={t.table_name} value={t.table_name}>
                      {t.table_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProject} disabled={!projectName.trim() || isSaving}>
              {isSaving && <Loader2 className="size-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
